import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { basicDesignApi } from '../lib/api.js';
import {
  bdLangOf,
  type BdArtifact,
  type BdFindingActionInput,
  type BdLang,
  type BdReview,
  type BdReviewSubmitBody,
  type BdRow,
} from '../lib/api/basic-design.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 画面の言語から、基本設計 API に渡す言語を決める。
 *
 * 言語を切り替えると `i18n.language` が変わり、キーに言語を含むクエリが読み直される。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 画面の表示言語から基本設計 API に渡す言語（en / ja）を決める
 * @graph-connects bdLangOf [calls] 言語の決定
 */
export function useBdLang(): BdLang {
  const { i18n } = useTranslation();
  return bdLangOf(i18n.language);
}

/**
 * 節の定義（名前・列・選択肢・上限・書き方の手がかり）を取得する。
 *
 * 定義はデプロイでしか変わらないので、取ったら読み直さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全節の定義を表示言語で取得する
 * @graph-connects /basic-design [calls] 節の定義取得API
 * @graph-connects basicDesignApi.artifacts [calls] 節の定義APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdArtifactDefs() {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.artifacts(lang),
    queryFn: () => basicDesignApi.artifacts(lang),
    staleTime: Infinity,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 節を保存した基本設計の一覧を取得する
 * @graph-connects /basic-design [calls] 基本設計一覧取得API
 * @graph-connects basicDesignApi.list [calls] 基本設計一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdList() {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.list(lang),
    queryFn: () => basicDesignApi.list(lang),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計 1 件の保存済みの節と同じ親チケットの基本設計を取得する
 * @graph-connects /basic-design [calls] 基本設計の成果物取得API
 * @graph-connects basicDesignApi.detail [calls] 基本設計の成果物APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdDetail(bdKey: string) {
  return useQuery({
    queryKey: queryKeys.basicDesign.detail(bdKey),
    queryFn: () => basicDesignApi.detail(bdKey),
    enabled: bdKey.length > 0,
  });
}

/**
 * 基本設計の次の 1 手を取得する。保存のたびに変わるので、保存のあとに読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計で次にやること 1 つを取得する
 * @graph-connects /basic-design [calls] 次の 1 手取得API
 * @graph-connects basicDesignApi.nextStep [calls] 次の 1 手APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdNextStep(bdKey: string) {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.nextStep(bdKey, lang),
    queryFn: () => basicDesignApi.nextStep(bdKey, lang),
    enabled: bdKey.length > 0,
  });
}

/**
 * 全体図と ER 図の Mermaid を取得する。全体の節を開いたときだけ読む。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図（業務フロー→画面→API→サービス→データ）と ER 図の Mermaid を取得する
 * @graph-connects /basic-design [calls] 図の取得API
 * @graph-connects basicDesignApi.diagrams [calls] 図のAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdDiagrams(bdKey: string, enabled: boolean) {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.diagrams(bdKey, lang),
    queryFn: () => basicDesignApi.diagrams(bdKey, lang),
    enabled: enabled && bdKey.length > 0,
  });
}

/**
 * 基本設計 1 件の Markdown を取得する。ボタンを押したときだけ読むので mutation にする
 * （押すたびに今の保存内容で書き出す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計 1 件を Markdown に書き出したものを取得する
 * @graph-connects /basic-design [calls] 書き出しAPI
 * @graph-connects basicDesignApi.document [calls] 書き出しAPIクライアント呼び出し
 */
export function useBdDocument(bdKey: string) {
  const lang = useBdLang();
  return useMutation({ mutationFn: () => basicDesignApi.document(bdKey, lang) });
}

/**
 * 節のフォーマット（書き方のルール）を取得する。開くまで取りに行かず、取ったら読み直さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の書き方（必須・値の意味・サンプル）を取得する
 * @graph-connects /basic-design [calls] フォーマット取得API
 * @graph-connects basicDesignApi.format [calls] フォーマットAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdFormat(artifact: BdArtifact, enabled: boolean) {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.format(artifact, lang),
    queryFn: () => basicDesignApi.format(artifact, lang),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * 節の保存の履歴（版・日時・保存した人）を取得する。開くまで取りに行かない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の保存の履歴を取得する
 * @graph-connects /basic-design [calls] 履歴取得API
 * @graph-connects basicDesignApi.revisions [calls] 履歴APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdRevisions(bdKey: string, artifact: BdArtifact, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.basicDesign.revisions(bdKey, artifact),
    queryFn: () => basicDesignApi.revisions(bdKey, artifact),
    enabled: enabled && bdKey.length > 0,
  });
}

/**
 * 節の過去の 1 版を行つきで取得する。版を選ぶまで取りに行かない。版は書き換わらないので読み直さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の過去の 1 版の行を取得する
 * @graph-connects /basic-design [calls] 過去の版の取得API
 * @graph-connects basicDesignApi.revision [calls] 過去の版のAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdRevision(bdKey: string, artifact: BdArtifact, version: number | null) {
  // 版を選ぶまでは読まない（enabled）ので、0 はキーを作るためだけの値
  const selected = version ?? 0;
  return useQuery({
    queryKey: queryKeys.basicDesign.revision(bdKey, artifact, selected),
    queryFn: () => basicDesignApi.revision(bdKey, artifact, selected),
    enabled: version !== null && bdKey.length > 0,
    staleTime: Infinity,
  });
}

/**
 * API の経路・エンティティ・画面から、それを変える基本設計を逆引きする。キーが空なら読まない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business API の経路・エンティティ・画面を変える基本設計の行と、その行が指す設計判断を逆引きする
 * @graph-connects /basic-design [calls] 逆引きAPI
 * @graph-connects basicDesignApi.targets [calls] 逆引きAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdTargets(key: string) {
  const lang = useBdLang();
  const trimmed = key.trim();
  return useQuery({
    queryKey: queryKeys.basicDesign.targets(trimmed, lang),
    queryFn: () => basicDesignApi.targets(trimmed, lang),
    enabled: trimmed.length > 0,
  });
}

/** 保存する節と行。 @graph-connects none */
export interface BdSaveInput {
  artifact: BdArtifact;
  /** 0 行は「該当なし」（任意の節だけ通る） */
  rows: BdRow[];
  /** 0 行で保存するときの理由（API が必須かを判定する） */
  notApplicableReason?: string;
}

/**
 * 節を保存する。
 *
 * 列ごとの上限・選択肢・参照・フローのノードの判定は API が持つ（MCP の upsert と同じ規則）。
 * 止めるほどではないものは結果の `warnings` / `issues` で返る。保存は版・次の 1 手・図・
 * 一覧の進み具合・履歴を変えるので、それらを読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節を product-api 経由で保存し、成果物・次の 1 手・図・レビュー・一覧・履歴を読み直す
 * @graph-connects /basic-design [calls] 節の保存API
 * @graph-connects basicDesignApi.save [calls] 節の保存APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSaveBdArtifact(bdKey: string) {
  const lang = useBdLang();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ artifact, rows, notApplicableReason }: BdSaveInput) =>
      basicDesignApi.save(bdKey, artifact, rows, lang, notApplicableReason),
    onSuccess: async (_result, { artifact }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.detail(bdKey) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.nextStepAll(bdKey) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.diagramsAll(bdKey) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.graphAll(bdKey) }),
        // 保存で中身の指紋が変わり、依頼済みのレビューが古くなる
        queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.reviewAll(bdKey) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.listAll() }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.basicDesign.revisions(bdKey, artifact),
        }),
      ]);
    },
  });
}

/**
 * 全体・ER・画面遷移の動く図を取得する。全体の節を開いたときだけ読む。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図・画面遷移図のノードと線を取得する
 * @graph-connects /basic-design [calls] 動く図の取得API
 * @graph-connects basicDesignApi.graph [calls] 動く図のAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdGraph(bdKey: string, enabled: boolean) {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.graph(bdKey, lang),
    queryFn: () => basicDesignApi.graph(bdKey, lang),
    enabled: enabled && bdKey.length > 0,
  });
}

/**
 * 基本設計のレビュー（状態・レビュアー・指摘・チェックリスト・観点）を取得する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューの状態・指摘・チェックリストと観点を取得する
 * @graph-connects /basic-design [calls] レビュー取得API
 * @graph-connects basicDesignApi.review [calls] レビューのAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdReview(bdKey: string) {
  const lang = useBdLang();
  return useQuery({
    queryKey: queryKeys.basicDesign.review(bdKey, lang),
    queryFn: () => basicDesignApi.review(bdKey, lang),
    enabled: bdKey.length > 0,
  });
}

/**
 * レビュアーの候補をニックネームで探す。空なら読まない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビュアーに指名できる人を Firestore users のニックネームで探す
 * @graph-connects /basic-design [calls] レビュアー候補取得API
 * @graph-connects basicDesignApi.reviewers [calls] レビュアー候補のAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdReviewers(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: queryKeys.basicDesign.reviewers(trimmed),
    queryFn: () => basicDesignApi.reviewers(trimmed),
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}

/**
 * レビューを変える操作の後に読み直すもの。レビューの要約は一覧と詳細にも出る。
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
function useInvalidateReview(bdKey: string) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.reviewAll(bdKey) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.detail(bdKey) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.nextStepAll(bdKey) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.listAll() }),
    ]);
  };
}

/**
 * レビューを依頼する（レビュアーはニックネーム）。指名の可否・自分を指名できないことの判定は API が持つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューを、ニックネームで指名したレビュアーに依頼する
 * @graph-connects /basic-design [calls] レビュー依頼API
 * @graph-connects basicDesignApi.requestReview [calls] レビュー依頼のAPIクライアント呼び出し
 * @graph-connects useInvalidateReview [calls] レビューの読み直し
 */
export function useRequestBdReview(bdKey: string) {
  const lang = useBdLang();
  const invalidate = useInvalidateReview(bdKey);
  return useMutation({
    mutationFn: (reviewer: string): Promise<BdReview> =>
      basicDesignApi.requestReview(bdKey, reviewer, lang),
    onSuccess: invalidate,
  });
}

/**
 * レビューの段階を提出する。誰が出せるか・チェックリストが揃っているかの判定は API が持つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューの段階（AI・レビュアー）を、指摘とチェックリストつきで提出する
 * @graph-connects /basic-design [calls] レビュー提出API
 * @graph-connects basicDesignApi.submitReview [calls] レビュー提出のAPIクライアント呼び出し
 * @graph-connects useInvalidateReview [calls] レビューの読み直し
 */
export function useSubmitBdReview(bdKey: string) {
  const lang = useBdLang();
  const invalidate = useInvalidateReview(bdKey);
  return useMutation({
    mutationFn: (body: BdReviewSubmitBody): Promise<BdReview> =>
      basicDesignApi.submitReview(bdKey, body, lang),
    onSuccess: invalidate,
  });
}

/**
 * 指摘を直した・見送る・取り消す・戻す。理由が要るかどうかの判定は API が持つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューの指摘を、作者が直した・見送る・取り消す・未解決に戻す
 * @graph-connects /basic-design [calls] 指摘の解決API
 * @graph-connects basicDesignApi.resolveFindings [calls] 指摘の解決のAPIクライアント呼び出し
 * @graph-connects useInvalidateReview [calls] レビューの読み直し
 */
export function useResolveBdFindings(bdKey: string) {
  const lang = useBdLang();
  const invalidate = useInvalidateReview(bdKey);
  return useMutation({
    mutationFn: (items: BdFindingActionInput[]): Promise<BdReview> =>
      basicDesignApi.resolveFindings(bdKey, items, lang),
    onSuccess: invalidate,
  });
}

/**
 * 共有のメモを取得する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計に付けた共有のメモ（Markdown）を取得する
 * @graph-connects /basic-design [calls] メモ取得API
 * @graph-connects basicDesignApi.notes [calls] メモのAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useBdNotes(bdKey: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.basicDesign.notes(bdKey),
    queryFn: () => basicDesignApi.notes(bdKey),
    enabled: enabled && bdKey.length > 0,
  });
}

/**
 * 共有のメモを保存する。上限と書ける人の判定は API が持つ。保存の結果には本文も日時も無いので、
 * メモを読み直し終えてから呼び出し元の onSuccess（編集を閉じる）に進ませる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計に付ける共有のメモ（Markdown）を保存し、メモを読み直す
 * @graph-connects /basic-design [calls] メモ保存API
 * @graph-connects basicDesignApi.saveNotes [calls] メモ保存のAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSaveBdNotes(bdKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (markdown: string) => basicDesignApi.saveNotes(bdKey, markdown),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.basicDesign.notes(bdKey) });
    },
  });
}
